import moment from 'moment';
import axios from './axios';

const normalizeRole = (role) => {
  if (!role) return undefined;
  const normalized = role.toLowerCase();
  if (normalized === 'administrator' || normalized === 'blood tester') {
    return normalized;
  }
  return undefined;
};

const buildUserChanges = (
  userDetails,
  originalUser,
  { password, passwordHasChanged, isEditingSelf },
) => {
  const { firstName, surname, role, email, username } = userDetails;
  const normalizedRole = normalizeRole(role);
  const isTargetAdministrator = normalizedRole === 'administrator';
  const changes = {};

  if (firstName) changes.firstName = firstName;
  if (surname) changes.surname = surname;
  if (email && email !== originalUser?.email) changes.email = email;
  if (username && username !== originalUser?.username) {
    changes.username = username;
  }
  if (passwordHasChanged && password) changes.password = password;
  if (!isEditingSelf && !isTargetAdministrator && normalizedRole) {
    changes.role = normalizedRole;
  }

  return changes;
};

// READ
export const getAllUsers = async (token) => {
  try {
    const response = await axios.get('/users', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const formattedData = response?.data?.data.map((user) => {
      return {
        ...user,
        createdAt: moment(user.createdAt).format('YYYY-MM-DD HH:mm:ss'),
        updatedAt: moment(user.updatedAt).format('YYYY-MM-DD HH:mm:ss'),
        fullname: `${user.firstName} ${user.surname}`,
        role: user.role
          ? user.role.charAt(0).toUpperCase() + user.role.slice(1)
          : 'Unknown',
        confirmedLabel: user.confirmed ? 'Confirmed' : 'Pending',
      };
    });
    return formattedData;
  } catch (error) {
    return [];
  }
};

export const getUserDetails = async (token, id) => {
  try {
    const response = await axios.get(`/user/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response?.data?.data;
  } catch (error) {
    return error.response;
  }
};

export const adminUpdateAnotherUser = async (
  token,
  userDetails,
  passwordHasChanged,
  password,
  currentPassword,
  isEditingSelf = false,
  originalUser = {},
) => {
  const changes = buildUserChanges(userDetails, originalUser, {
    password,
    passwordHasChanged,
    isEditingSelf,
  });

  const emailChanged = changes.email !== undefined;
  const needsCurrentPassword =
    isEditingSelf && (passwordHasChanged || emailChanged);

  const body = {
    changes,
    ...(needsCurrentPassword && currentPassword ? { currentPassword } : {}),
  };

  try {
    const response = await axios.patch(
      `/user/${userDetails?.id}`,
      body,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response;
  } catch (err) {
    return err.response;
  }
};

export const deleteUser = async (
  token,
  id,
  password,
  isSelectedUserAdmin,
  adminUserWasSelected
) => {
  const bodyWithPassword = isSelectedUserAdmin || adminUserWasSelected;
  try {
    if (bodyWithPassword) {
      const response = await axios.delete(`/user/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { password },
      });
      return response;
    } else {
      const response = await axios.delete(`/user/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response;
    }
  } catch (err) {
    return err.response;
  }
};

export const setUserConfirmed = async (token, userId, confirmed) => {
  try {
    const response = await axios.patch(
      `/user/${userId}/confirm`,
      { confirmed },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response;
  } catch (err) {
    return err.response;
  }
};

export const resendUserConfirmationEmail = async (token, userId) => {
  try {
    const response = await axios.post(
      `/user/${userId}/resend-confirmation`,
      {},
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response;
  } catch (err) {
    return err.response;
  }
};

export const adminCreateUser = async (token, userDetails, password) => {
  try {
    const { firstName, surname, email, role } = userDetails;

    const body = {
      firstName,
      surname,
      email,
      role: role?.replace('-', ' '),
      password,
      lang: 'en-GB',
    };

    const response = await axios.post(
      `/user`,
      {
        ...body,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response;
  } catch (err) {
    return err.response;
  }
};
